<template>
  <v-dialog v-model="dialog" max-width="700">
    <v-card>
      <v-card-title class="blue darken-1 white--text">
        <p class="d-flex align-center mb-1">
          <v-icon dark class="mr-2">mdi-file-eye-outline</v-icon>
          {{ $t("PANEL.MEDIA.INFO_DIALOG.TITLE") }}
        </p>
        <v-spacer></v-spacer>
        <v-btn icon color="white" @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-container class="px-0">
          <v-row>
            <v-col cols="12" sm="6">
              <div class="media-item">
                <file-thumbnailer v-if="value.name" :value="value" viewport-height="auto" />
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <p class="subtitle-1 mb-2 blue--text text--darken-1">
                <b>{{ $t("PANEL.MEDIA.INFO_DIALOG.INFO_TITLE") }}</b>
              </p>
              <p class="mb-1">
                <b>{{ $t("PANEL.MEDIA.INFO_DIALOG.INFO_FILENAME") }}:</b>
                {{ value.name }}
              </p>
              <p class="mb-1">
                <b>{{ $t("PANEL.MEDIA.INFO_DIALOG.INFO_FILE_TYPE") }}:</b>
                {{ value.extension }}
              </p>
              <p class="mb-1">
                <b>{{ $t("PANEL.MEDIA.INFO_DIALOG.INFO_FILE_SIZE") }}:</b>
                {{ value.size }}
              </p>
              <p class="mb-1">
                <b>{{ $t("PANEL.MEDIA.INFO_DIALOG.INFO_DATE_OF_UPLOAD") }}:</b>
                {{ value.time }}
              </p>
              <v-divider class="mt-5 mb-4"></v-divider>
              <p class="subtitle-1 mb-2 blue--text text--darken-1">
                <b>{{ $t("PANEL.MEDIA.INFO_DIALOG.INFO_ACTION_TITLE") }}</b>
              </p>
              <v-btn v-if="isBaseImage(value.type)" small color="deep-purple" dark class="text-capitalize mr-3" @click="imageCropper = true">
                <v-icon size="15" class="mr-1">mdi-image-edit</v-icon>
                {{ $t("PANEL.MEDIA.INFO_DIALOG.INFO_ACTION_IMAGE_EDIT_BTN") }}
              </v-btn>
              <v-btn small color="red" dark class="text-capitalize" @click="deleteFile(value.name)">
                <v-icon size="15" class="mr-1">mdi-trash-can</v-icon>
                {{ $t("PANEL.MEDIA.INFO_DIALOG.INFO_ACTION_IMAGE_DELETE_BTN") }}
              </v-btn>
            </v-col>
            <v-col cols="12" class="py-0">
              <div>
                <p class="subtitle-1 mb-1">
                  <span class="d-inline-block mr-3">
                    <b>{{ $t("PANEL.MEDIA.INFO_DIALOG.INFO_FILE_PATH_TITLE") }}</b>
                  </span>
                  <v-btn x-small color="primary" class="mr-1 text-capitalize" @click="copyUrl(url)">
                    {{ $t("PANEL.MEDIA.INFO_DIALOG.INFO_FILE_PATH_COPY_BTN") }}
                    <v-icon class="ml-1" size=".85rem">mdi-content-copy</v-icon>
                  </v-btn>
                  <v-btn x-small color="primary" :href="url" class="text-capitalize" target="_blank">
                    {{ $t("PANEL.MEDIA.INFO_DIALOG.INFO_FILE_OPEN_NEW_TAB_BTN") }}
                    <v-icon class="ml-1" size=".85rem">mdi-open-in-new</v-icon>
                  </v-btn>
                </p>
                <v-text-field disabled class="pt-0" :value="url" ref="fileUrl"></v-text-field>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>
    <!--  Image Cropper  -->
    <image-cropper v-if="value.name" v-model="imageCropper" :image="valueWithUrl" @cropped="cropperChanged" />
    <!--  ./Image Cropper  -->
    <v-snackbar :timeout="snackbar.timeout" :value="snackbar.model" absolute shaped bottom color="green">
      <v-icon left>mdi-check</v-icon>
      {{ $t("PANEL.MEDIA.NOTIFY.INFO_FILE_PATH_COPIED") }}
    </v-snackbar>
  </v-dialog>
</template>

<script>
import FileThumbnailer from "./FileThumbnailer.vue";
import { mapGetters } from "vuex";
import MediaService from "@/services/MediaService";
import ImageCropper from "./ImageCropper.vue";

export default {
  components: { FileThumbnailer, ImageCropper },
  name: "FileViewer",
  props: {
    value: {
      required: true
    }
  },
  data: () => ({
    dialog: false,
    imageCropper: false,
    snackbar: {
      model: false,
      timeout: 4100,
      setTimeout: null
    }
  }),
  computed: {
    ...mapGetters({
      isBaseImage: "Media/isBaseImage"
    }),
    valueWithUrl() {
      return {
        ...this.value,
        url: this.url
      };
    },
    url() {
      return `${this.$ApiUrl}${this.value.format}/${this.value.name}`;
    }
  },
  methods: {
    copyUrl() {
      let copyText = this.$refs.fileUrl.$el.querySelector("input");
      copyText.removeAttribute("disabled");
      copyText.select();
      copyText.setSelectionRange(0, 99999); /* For mobile devices */
      document.execCommand("copy");
      copyText.setAttribute("disabled", "true");
      this.snackbar.model = true;
    },
    deleteFile(fileName) {
      MediaService.removeFiles(fileName).then(res => {
        if (res.status === 200) {
          this.dialog = false;
          this.$emit("deleted", fileName);
        } else {
          this.$snotify.error(res.data.errMessage, this.$t("PANEL.NOTIFY.ERROR"));
        }
      });
    },
    cropperChanged(file) {
      this.$emit("change", file);
      this.dialog = false;
    }
  },
  watch: {
    dialog(val) {
      if (val === false) {
        this.$emit("input", {});
      }
    },
    value() {
      if (JSON.stringify(this.value) != "{}") {
        this.dialog = true;
      }
    },
    "snackbar.model": {
      handler() {
        if (!this.snackbar.model) return;
        if (this.snackbar.setTimeout) clearTimeout(this.snackbar.setTimeout);

        this.snackbar.setTimeout = setTimeout(() => {
          this.snackbar.model = false;
        }, this.snackbar.timeout);
      }
    }
  }
};
</script>
